<template>
  <el-dialog
    :title="mode === 'add' ? '新建价目表' : '编辑价目表'"
    :visible.sync="dialogVisible"
    width="640px"
    append-to-body
    :close-on-click-modal="false"
    @closed="handleClosed"
  >
    <el-form ref="form" :model="form" :rules="rules" label-width="130px" v-loading="submitting">
      <el-form-item label="价目表名称" prop="name">
        <el-input v-model="form.name" placeholder="请输入价目表名称" maxlength="50" />
      </el-form-item>
      <el-form-item label="项目类型" prop="projectType">
        <el-select
          v-model="form.projectType"
          placeholder="请选择项目类型"
          style="width: 100%"
          :disabled="mode === 'edit'"
        >
          <el-option
            v-for="item in projectTypeDic"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <div v-if="mode === 'edit'" class="form-tip">项目类型创建后不可修改</div>
      </el-form-item>
      <el-form-item label="价盘编号" prop="code">
        <el-input
          v-model="form.code"
          placeholder="请输入价盘编号（组织内唯一）"
          maxlength="50"
          :disabled="mode === 'edit'"
        />
      </el-form-item>
      <el-form-item label="匹配优先级" prop="priority">
        <el-input-number
          v-model="form.priority"
          :min="0"
          :precision="0"
          controls-position="right"
          style="width: 100%"
          placeholder="越小越优先"
        />
        <div class="form-tip">普通价盘按优先级从小到大逐张匹配，命中即停</div>
      </el-form-item>
      <el-form-item label="是否兜底" prop="isFallback">
        <el-radio-group v-model="form.isFallback" @change="handleFallbackChange">
          <el-radio-button label="NO">普通</el-radio-button>
          <el-radio-button label="YES">兜底</el-radio-button>
        </el-radio-group>
        <div class="form-tip">普通价盘都没命中时使用兜底价盘</div>
      </el-form-item>
      <el-form-item v-if="form.isFallback === 'YES' && !isCeiling" label="兜底基价" prop="basePrice">
        <el-input-number
          v-model="form.basePrice"
          :min="0"
          :precision="2"
          controls-position="right"
          style="width: 100%"
          placeholder="兜底基价（元/㎡）"
        />
        <div class="form-tip">兜底表未配商户价时使用该基价，兜底盘必填</div>
      </el-form-item>
      <!-- 型号基价仅「吊顶 + 是否兜底=兜底」时提供配置入口 -->
      <el-form-item v-if="isCeiling && form.isFallback === 'YES'" label="型号基价">
        <el-button size="small" icon="el-icon-setting" @click="openFilterDialog">基价明细</el-button>
        <span v-if="filterTouched" class="filter-count">
          已暂存 {{ filterPrices.length }} 个型号基价（保存价目表后生效）
        </span>
        <div class="form-tip">
          吊顶兜底走型号基价：一个扣板型号一个兜底基价，仅兜底环节生效；价格清空的型号不参与兜底
        </div>
      </el-form-item>
      <el-form-item :label="thresholdLabel" prop="threshold">
        <el-input-number
          v-model="form.threshold"
          :min="0"
          :precision="2"
          controls-position="right"
          style="width: 100%"
          :placeholder="thresholdPlaceholder"
        />
      </el-form-item>
      <el-form-item label="保底补贴单价" prop="subsidyPrice">
        <el-input-number
          v-model="form.subsidyPrice"
          :min="0"
          :precision="2"
          controls-position="right"
          style="width: 100%"
          placeholder="选填，保底补贴单价（元/㎡）"
        />
      </el-form-item>
      <el-form-item label="生效开始时间" prop="effectiveStartTime">
        <el-date-picker
          v-model="form.effectiveStartTime"
          type="datetime"
          value-format="yyyy-MM-dd HH:mm:ss"
          placeholder="空 = 立即生效"
          style="width: 100%"
        />
      </el-form-item>
      <el-form-item label="生效结束时间" prop="effectiveEndTime">
        <el-date-picker
          v-model="form.effectiveEndTime"
          type="datetime"
          value-format="yyyy-MM-dd HH:mm:ss"
          placeholder="空 = 长期有效"
          style="width: 100%"
        />
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-radio-group v-model="form.status">
          <el-radio-button label="ENABLE">启用</el-radio-button>
          <el-radio-button label="DISABLE">停用</el-radio-button>
        </el-radio-group>
      </el-form-item>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" :loading="submitting" @click="handleSubmit">确定</el-button>
    </span>

    <!-- 吊顶型号基价明细弹窗 -->
    <el-dialog
      title="型号基价明细"
      :visible.sync="filterDialogVisible"
      width="640px"
      append-to-body
      :close-on-click-modal="false"
      @closed="handleFilterClosed"
    >
      <div class="form-tip" style="margin-bottom: 10px">
        型号基价 = 不管哪个商户，该扣板型号兜底多少钱一平米，仅兜底环节生效；不需要的型号把价格清空即可（该型号不参与兜底）
      </div>
      <el-table
        v-loading="filterLoading"
        :data="filterRows"
        border
        stripe
        size="small"
        max-height="420"
      >
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="goodsName" label="所属商品" min-width="140" show-overflow-tooltip />
        <el-table-column prop="goodsFilterName" label="扣板型号" min-width="120" show-overflow-tooltip />
        <el-table-column label="兜底基价（元/㎡）" width="190" align="center">
          <template slot-scope="scope">
            <el-input-number
              v-model="scope.row.price"
              :min="0"
              :precision="2"
              controls-position="right"
              size="small"
              style="width: 100%"
              placeholder="清空 = 不参与兜底"
            />
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="handleFilterClose">完成</el-button>
      </span>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { projectTypeDic } from "@/const/work/waterProof/merchantPriceOption";
import {
  addPriceBook,
  updatePriceBook,
  findPriceBookFilterPrice,
} from "@/api/work/waterProof/merchantPrice";
import { findPureWaterGoodsByRootDepartId } from "@/api/salesCloud/goodsQr";

const EMPTY_FORM = {
  name: "",
  projectType: "",
  code: "",
  priority: 1,
  isFallback: "NO",
  basePrice: null,
  threshold: null,
  subsidyPrice: null,
  effectiveStartTime: null,
  effectiveEndTime: null,
  status: "ENABLE",
};

export default {
  name: "PriceBookFormDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "add", // add | edit
    },
    priceBook: {
      type: Object,
      default: null,
    },
  },
  data() {
    const validateBasePrice = (rule, value, callback) => {
      // 吊顶兜底盘不配 basePrice（走型号基价），仅防水/回填兜底盘必填
      if (
        !this.isCeiling &&
        this.form.isFallback === "YES" &&
        (value === null || value === undefined || value === "")
      ) {
        callback(new Error("兜底盘必须填写兜底基价"));
      } else {
        callback();
      }
    };
    const validateEndTime = (rule, value, callback) => {
      if (value && this.form.effectiveStartTime && value < this.form.effectiveStartTime) {
        callback(new Error("生效结束时间不能早于开始时间"));
      } else {
        callback();
      }
    };
    return {
      projectTypeDic,
      dialogVisible: false,
      submitting: false,
      form: { ...EMPTY_FORM },
      // 吊顶型号基价：弹窗表格行 + 已暂存明细（filterTouched=false 时提交不带 filterPrices，后端不动已有数据）
      filterDialogVisible: false,
      filterLoading: false,
      filterRows: [],
      filterPrices: [],
      filterTouched: false,
      rules: {
        name: [{ required: true, message: "请输入价目表名称", trigger: "blur" }],
        projectType: [{ required: true, message: "请选择项目类型", trigger: "change" }],
        code: [{ required: true, message: "请输入价盘编号", trigger: "blur" }],
        priority: [{ required: true, message: "请输入匹配优先级", trigger: "blur" }],
        basePrice: [{ validator: validateBasePrice, trigger: "blur" }],
        effectiveEndTime: [{ validator: validateEndTime, trigger: "change" }],
      },
    };
  },
  computed: {
    isCeiling() {
      return this.form.projectType === "HANG_CEILING";
    },
    thresholdLabel() {
      return this.isCeiling ? "面积保底阈值" : "面积保底阈值";
    },
    thresholdPlaceholder() {
      return this.isCeiling
        ? "选填，单位面积材料费上限（元/㎡）"
        : "选填，面积不足按阈值计算（㎡）";
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        this.dialogVisible = val;
        if (val) this.initForm();
      },
    },
    dialogVisible(val) {
      this.$emit("update:visible", val);
    },
  },
  methods: {
    // 回显时间：兼容时间戳（转 "yyyy-MM-dd HH:mm:ss"）与后端返回的展示格式字符串
    toDateStr(v) {
      if (v == null || v === "") return null;
      if (typeof v === "number" || /^\d+$/.test(String(v))) {
        const d = new Date(Number(v));
        if (isNaN(d.getTime())) return null;
        const pad = (n) => (n < 10 ? "0" + n : "" + n);
        return (
          d.getFullYear() +
          "-" +
          pad(d.getMonth() + 1) +
          "-" +
          pad(d.getDate()) +
          " " +
          pad(d.getHours()) +
          ":" +
          pad(d.getMinutes()) +
          ":" +
          pad(d.getSeconds())
        );
      }
      return v;
    },
    initForm() {
      if (this.mode === "edit" && this.priceBook) {
        this.form = {
          name: this.priceBook.name,
          projectType: this.priceBook.projectType,
          code: this.priceBook.code,
          priority: this.priceBook.priority,
          isFallback: this.priceBook.isFallback || "NO",
          basePrice: this.priceBook.basePrice != null ? this.priceBook.basePrice : null,
          threshold: this.priceBook.threshold != null ? this.priceBook.threshold : null,
          subsidyPrice: this.priceBook.subsidyPrice != null ? this.priceBook.subsidyPrice : null,
          effectiveStartTime: this.toDateStr(this.priceBook.effectiveStartTime),
          effectiveEndTime: this.toDateStr(this.priceBook.effectiveEndTime),
          status: this.priceBook.status || "ENABLE",
        };
      } else {
        this.form = { ...EMPTY_FORM };
      }
      this.filterRows = [];
      this.filterPrices = [];
      this.filterTouched = false;
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
      });
    },
    parseData(res) {
      return typeof res.data === "string" ? JSON.parse(res.data) : res.data;
    },
    // 切换「是否兜底」时清空已暂存的型号基价，避免普通盘误带 filterPrices
    handleFallbackChange() {
      this.filterDialogVisible = false;
      this.filterRows = [];
      this.filterPrices = [];
      this.filterTouched = false;
    },
    // 打开型号基价明细弹窗（仅吊顶）
    openFilterDialog() {
      this.filterDialogVisible = true;
      this.loadFilterRows();
    },
    // 加载全部扣板型号（calculateType=2 商品的 filters），编辑时回显已配基价
    // 价格默认值 = 已配基价 ?? filter.customerPrice（商品型号默认价反显）
    loadFilterRows() {
      this.filterLoading = true;
      const goodsReq = findPureWaterGoodsByRootDepartId("HANG_CEILING");
      const priceReq =
        this.mode === "edit" && this.priceBook
          ? findPriceBookFilterPrice(this.priceBook.id)
          : Promise.resolve(null);
      Promise.all([goodsReq, priceReq])
        .then(([goodsRes, priceRes]) => {
          const configured = {};
          if (priceRes && priceRes.code === "200") {
            const list = this.parseData(priceRes) || [];
            (Array.isArray(list) ? list : []).forEach((p) => {
              configured[p.goodsFilterId] = p.price;
            });
          }
          const rows = [];
          if (goodsRes.code === "200") {
            const data = this.parseData(goodsRes) || [];
            (Array.isArray(data) ? data : [])
              .filter((item) => String(item.calculateType) === "2")
              .forEach((item) => {
                (item.filters || []).forEach((f) => {
                  const configuredPrice = configured[f.id];
                  rows.push({
                    goodsFilterId: f.id,
                    goodsFilterName: f.value,
                    goodsName: item.name,
                    price:
                      configuredPrice != null
                        ? configuredPrice
                        : f.customerPrice != null
                        ? f.customerPrice
                        : null,
                  });
                });
              });
          }
          this.filterRows = rows;
        })
        .catch((err) => {
          console.log("loadFilterRows error:", err);
          this.$message.error("扣板型号加载异常");
        })
        .finally(() => {
          this.filterLoading = false;
        });
    },
    // 点击「完成」：立即暂存型号基价并关闭弹窗（不等 @closed 动画，否则快速点「确定」时 filterTouched 未生效导致 filterPrices 不提交）
    handleFilterClose() {
      this.stashFilterPrices();
      this.filterDialogVisible = false;
    },
    // 弹窗通过 ESC 等其他方式关闭时的兜底暂存
    handleFilterClosed() {
      this.stashFilterPrices();
    },
    // 暂存到 filterPrices（不提交）；价格清空的型号不参与兜底
    stashFilterPrices() {
      this.filterPrices = this.filterRows
        .filter((r) => r.price !== null && r.price !== undefined && r.price !== "")
        .map((r) => ({ goodsFilterId: r.goodsFilterId, price: r.price }));
      this.filterTouched = true;
    },
    handleSubmit() {
      this.$refs.form.validate((valid) => {
        if (!valid) return;
        this.submitting = true;
        const payload = {
          name: this.form.name,
          priority: this.form.priority,
          isFallback: this.form.isFallback,
          // 吊顶兜底盘不配 basePrice（传了后端报错），走型号基价 filterPrices
          basePrice:
            !this.isCeiling && this.form.isFallback === "YES" ? this.form.basePrice : null,
          threshold: this.form.threshold,
          subsidyPrice: this.form.subsidyPrice,
          effectiveStartTime: this.form.effectiveStartTime,
          effectiveEndTime: this.form.effectiveEndTime,
          status: this.form.status,
        };
        // 吊顶兜底盘：仅打开过基价明细弹窗才提交 filterPrices；不传则后端不动已有基价数据
        if (this.isCeiling && this.form.isFallback === "YES" && this.filterTouched) {
          payload.filterPrices = this.filterPrices;
        }
        const req =
          this.mode === "edit"
            ? updatePriceBook({ id: this.priceBook.id, ...payload })
            : addPriceBook({
                ...payload,
                projectType: this.form.projectType,
                code: this.form.code,
              });
        req
          .then((res) => {
            if (res.code === "200") {
              this.$message.success(this.mode === "edit" ? "编辑成功" : "新建成功");
              this.dialogVisible = false;
              this.$emit("submit-success");
            } else {
              this.$message.error(res.message || "保存失败");
            }
          })
          .catch((err) => {
            console.log("PriceBookFormDialog submit error:", err);
            this.$message.error("保存异常");
          })
          .finally(() => {
            this.submitting = false;
          });
      });
    },
    handleClosed() {
      this.form = { ...EMPTY_FORM };
      this.filterRows = [];
      this.filterPrices = [];
      this.filterTouched = false;
      this.$refs.form && this.$refs.form.clearValidate();
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
.filter-count {
  margin-left: 10px;
  font-size: 12px;
  color: #e6a23c;
}
</style>
